import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface VerificacaoEficacia {
  id: string;
  formulario_plano_acao_id: string;
  eficaz?: boolean | null;
  criterios_avaliacao?: string | null;
  resultado_obtido?: string | null;
  acoes_adicionais?: string | null;
  verificado_por?: string | null;
  verificado_em?: string | null;
  observacoes?: string | null;
  criado_em: string;
  atualizado_em: string;
  verificador?: { id: string; name: string } | null;
}

interface VerificacaoEficaciaInsert {
  id?: string;
  formulario_plano_acao_id: string;
  eficaz?: boolean | null;
  criterios_avaliacao?: string | null;
  resultado_obtido?: string | null;
  acoes_adicionais?: string | null;
  verificado_por?: string | null;
  observacoes?: string | null;
}

export function useVerificacaoEficaciaFormulario(formularioPlanoAcaoId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['verificacao-eficacia-formulario', formularioPlanoAcaoId],
    queryFn: async () => {
      if (!formularioPlanoAcaoId) return null;
      const { data, error } = await supabase
        .from('verificacao_eficacia_formulario')
        .select(`
          *,
          verificador:users!verificacao_eficacia_formulario_verificado_por_fkey(id, name)
        `)
        .eq('formulario_plano_acao_id', formularioPlanoAcaoId)
        .maybeSingle();
      if (error) throw error;
      return data as VerificacaoEficacia | null;
    },
    enabled: !!formularioPlanoAcaoId
  });

  const upsertMutation = useMutation({
    mutationFn: async (data: VerificacaoEficaciaInsert) => {
      const payload = {
        ...data,
        verificado_em: new Date().toISOString(),
        atualizado_em: new Date().toISOString()
      };

      const { data: result, error } = await supabase
        .from('verificacao_eficacia_formulario')
        .upsert(payload)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['verificacao-eficacia-formulario', formularioPlanoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['formulario-planos-acao'] });
      toast({ title: 'Verificação de eficácia salva com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao salvar verificação', description: error.message, variant: 'destructive' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('verificacao_eficacia_formulario')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['verificacao-eficacia-formulario', formularioPlanoAcaoId] });
      toast({ title: 'Verificação excluída' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao excluir verificação', description: error.message, variant: 'destructive' });
    }
  });

  return {
    verificacao: query.data,
    isLoading: query.isLoading,
    error: query.error,
    saveVerificacao: upsertMutation.mutate,
    deleteVerificacao: deleteMutation.mutate,
    isSaving: upsertMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
}
